import { onSessionEnded, reloadForSession } from '../../api/client';
import type { DraftOutbox } from './draftOutbox';

/** A signed-in page's answer to its session ending (a 401), while it may hold unsynced drafts. */
export type SessionEndHold = {
  /** Signed out, but kept open because leaving would lose drafts. */
  held(): boolean;
  subscribe(listener: () => void): () => void;
  /** Hand the 401 back to whatever handled it before. */
  stop(): void;
};

type Options = {
  /** Leave the page signed out. Called only at a moment the outbox holds nothing. */
  endSession?: () => void;
  /** Whether the service still knows this page's account (signed in again elsewhere). */
  stillSignedIn: () => Promise<boolean>;
};

/**
 * Ending the session — an account suspended, or signed out in another tab —
 * must not take this page's unsynced drafts with it: they live only in memory
 * (FR-007a), so any reload or navigation away would lose them.
 *
 * The page leaves only at a moment the outbox is empty, checked in the same
 * synchronous step that leaves, so no draft can exist and be lost. With work
 * unsynced, the page is held instead: sync pauses on its own ("needs sign-in"),
 * and once the person signs in again anywhere, the next sync carries the
 * drafts out. When the outbox drains — synced, or discarded by the person —
 * the hold asks the service whether this account is signed in again: if so
 * the page carries on, otherwise it leaves signed out as before.
 */
export function holdSessionEndWhileUnsynced(
  outbox: DraftOutbox,
  { endSession = reloadForSession, stillSignedIn }: Options,
): SessionEndHold {
  const listeners = new Set<() => void>();
  let isHeld = false;
  let ended = false;
  let checking = false;

  const setHeld = (next: boolean) => {
    if (next === isHeld) return;
    isHeld = next;
    listeners.forEach((listener) => listener());
  };

  const sessionEnded = () => {
    if (ended) return;
    if (outbox.hasPendingWork()) {
      setHeld(true);
      return;
    }
    ended = true;
    endSession();
  };

  const checkSession = async () => {
    if (checking) return;
    checking = true;
    let back = false;
    try {
      back = await stillSignedIn();
    } catch {
      back = false;
    } finally {
      checking = false;
    }
    if (back) setHeld(false);
    else sessionEnded();
  };

  const restoreHandler = onSessionEnded(sessionEnded);
  const stopWatching = outbox.subscribe(() => {
    if (isHeld && !outbox.hasPendingWork()) void checkSession();
  });

  return {
    held: () => isHeld,
    subscribe: (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    stop: () => {
      stopWatching();
      restoreHandler();
    },
  };
}
